import { formatCurrencyValue } from '@/lib/currencyFormatting';
import { defaultLocale, resolveLocale } from '@/lib/i18n/config';

export function getProductImage(product) {
  if (product?.coverImage) {
    return product.coverImage;
  }

  if (Array.isArray(product?.imagesPaths) && product.imagesPaths[0]) {
    return product.imagesPaths[0];
  }

  return null;
}

export function getProductCategories(product) {
  return Array.isArray(product?.categories)
    ? product.categories.map((category) => category?.name).filter(Boolean)
    : [];
}

function resolveProductCurrency(product) {
  if (typeof product?.currency === 'string' && product.currency.trim()) {
    return product.currency.trim();
  }

  const amount = Number(product?.price);
  const prices = product?.prices || {};

  if (Number.isFinite(amount)) {
    const matchingCurrency = Object.entries(prices).find(
      ([, value]) => Number(value) === amount,
    );

    if (matchingCurrency?.[0]) {
      return matchingCurrency[0];
    }
  }

  const currencies = Object.keys(product?.prices || {});
  return currencies[0] || 'USD';
}

export function getProductCurrency(product) {
  return resolveProductCurrency(product);
}

export function formatCurrencyAmount(
  amount,
  currency = 'USD',
  locale = defaultLocale,
) {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount)) {
    return null;
  }

  return formatCurrencyValue(numericAmount, {
    currency,
    locale: resolveLocale(locale),
    maximumFractionDigits: numericAmount % 1 === 0 ? 0 : 2,
  });
}

export function formatProductPrice(product, locale = defaultLocale) {
  const amount = Number(product?.price);

  if (!Number.isFinite(amount)) {
    return null;
  }

  return formatCurrencyAmount(amount, resolveProductCurrency(product), locale);
}

export function getProductReviewSummary(product) {
  const rating = Number(product?.rating) || 0;
  const count = Number(product?.numReviews) || 0;

  if (!count) {
    return 'No reviews yet';
  }

  return `${rating.toFixed(1)} / 5 (${count} review${count === 1 ? '' : 's'})`;
}

export function getProductStockState(product) {
  const count = Number(product?.countInStock) || 0;

  if (count <= 0) {
    return { tone: 'out', label: 'Out of stock' };
  }

  if (count <= 5) {
    return { tone: 'low', label: `Low stock (${count})` };
  }

  return { tone: 'in', label: 'In stock' };
}
